import React, { useState, useEffect } from 'react';
import { X, Shield, User as UserIcon, Upload, Image, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { User } from '../types';
import { rolesService } from '../services/rolesService';
import { UserRole } from '../types/roles';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: Partial<User>) => void;
  user?: User | null;
  error?: string;
  success?: string;
  onClearError?: () => void;
  onClearSuccess?: () => void;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  user,
  error,
  success,
  onClearError,
  onClearSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user' as User['role'],
    roleId: '',
    isActive: true,
    hasCrmAccess: false,
    isCollaborator: false,
    sendInvite: false
  });
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadRoles();
      setFormError(null);
    }
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        roleId: (user as any).role_id || '',
        isActive: user.isActive,
        avatar: user.avatar || '',
        hasCrmAccess: (user as any).has_crm_access || false,
        isCollaborator: !!(user as any).has_crm_access,
        sendInvite: false
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'user',
        roleId: '',
        isActive: true,
        avatar: '',
        hasCrmAccess: false,
        isCollaborator: false,
        sendInvite: false
      });
    }
  }, [user, isOpen]);

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const allRoles = await rolesService.getAllRoles();
      setRoles(allRoles);
    } catch (err) {
      console.error('Error loading roles:', err);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('El nombre no puede estar vacío');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setFormError('El formato del email no es válido');
      return;
    }

    if (!formData.roleId) {
      setFormError('Debes seleccionar un rol');
      return;
    }

    onSave({ ...formData, email: formData.email.trim().toLowerCase(), name: formData.name.trim() });
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          avatar: result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900">Error al crear usuario</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={onClearError}
                  className="text-red-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900">Usuario creado exitosamente</h4>
                  <p className="text-sm text-green-700 mt-1 whitespace-pre-line">{success}</p>
                </div>
                <button
                  type="button"
                  onClick={onClearSuccess}
                  className="text-green-400 hover:text-green-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Foto de perfil
            </label>
            <div className="flex items-center space-x-4">
              {formData.avatar ? (
                <div className="flex items-center space-x-4">
                  <img 
                    src={formData.avatar} 
                    alt="Avatar del usuario" 
                    className="w-16 h-16 object-cover border border-gray-200 rounded-full"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, avatar: '' }))}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  id="avatar-upload"
                />
                <label
                  htmlFor="avatar-upload"
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors text-sm"
                >
                  <Upload className="w-4 h-4" />
                  <span>Subir Foto</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG. Máximo 2MB.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ej: Ana García"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ana@ceroideas.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol *
            </label>
            {loadingRoles ? (
              <div className="flex items-center justify-center py-2">
                <Loader className="w-5 h-5 animate-spin text-blue-600" />
              </div>
            ) : (
              <select
                value={formData.roleId}
                onChange={(e) => {
                  const selectedRole = roles.find(r => r.id === e.target.value);
                  setFormData({
                    ...formData,
                    roleId: e.target.value,
                    role: selectedRole?.name === 'superadmin' ? 'superadmin' : 'user'
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar rol</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.display_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Role Description */}
          {formData.roleId && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    {roles.find(r => r.id === formData.roleId)?.display_name}
                  </h4>
                  <p className="text-xs text-gray-600 mt-1">
                    {roles.find(r => r.id === formData.roleId)?.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Usuario activo
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="hasCrmAccess"
                checked={formData.hasCrmAccess}
                onChange={(e) => setFormData({ ...formData, hasCrmAccess: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="hasCrmAccess" className="ml-2 block text-sm text-gray-900">
                Tiene acceso al CRM
              </label>
            </div>

            {formData.hasCrmAccess && (
              <div className="ml-6 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  Se enviará un email al usuario con las instrucciones de acceso al CRM.
                </p>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isCollaborator"
                checked={formData.isCollaborator}
                onChange={(e) => setFormData({ ...formData, isCollaborator: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isCollaborator" className="ml-2 block text-sm text-gray-900">
                Incluir en Plantilla
              </label>
            </div>

            {formData.isCollaborator && (
              <div className="ml-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  El usuario aparecerá en la sección de Plantilla y podrá gestionar sus horas, proyectos y facturas.
                </p>
              </div>
            )}
          </div>

          {!user && (
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendInvite"
                  checked={formData.sendInvite}
                  onChange={(e) => setFormData({ ...formData, sendInvite: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="sendInvite" className="ml-2 block text-sm text-gray-900">
                  Enviar invitación por email
                </label>
              </div>

              {formData.sendInvite ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700">
                    <strong>Invitación por email:</strong> El usuario recibirá un email de Supabase con un enlace para establecer su contraseña.
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    No necesitas proporcionar una contraseña temporal.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Creación directa:</strong> Se creará el usuario con una contraseña temporal que deberá cambiar en el primer acceso.
                  </p>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-blue-600">
                      <strong>Contraseña temporal:</strong> TempPassword123!
                    </p>
                    <p className="text-xs text-blue-600">
                      Deberás compartir esta contraseña con el usuario manualmente.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {user ? 'Actualizar' : 'Crear'} Usuario
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;